import { useEffect, useRef, useState } from "react";
import { Loader2, UploadCloud } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "./ui/dialog";
import { StravaConnectButton } from "./StravaConnectButton";
import { authUpdateProfile, stravaConnect, stravaStatus } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toKg, weightValue } from "../lib/format";
import { buildProfileUpdate, profileFieldErrors } from "../lib/profileUpdate";
import {
  clearOnboardingDraft,
  readOnboardingDraft,
  writeOnboardingDraft,
} from "../lib/onboarding";
import { athleteStravaView } from "../lib/stravaStatus";
import { toast } from "sonner";

const inputCls = "app-control font-mono text-sm w-full";

const EMPTY_FORM = {
  default_sport: "",
  dob: "",
  height_cm: "",
  weight_kg: "",
  gender: "",
  experience_level: "",
  ftp: "",
  max_hr: "",
  resting_hr: "",
  has_power_meter: "",
  unit_system: "",
};

function formFromUser(user) {
  const draft = readOnboardingDraft(user?.user_id);
  if (draft && typeof draft === "object") return { ...EMPTY_FORM, ...draft };
  return {
    ...EMPTY_FORM,
    default_sport: user?.default_sport || "",
    dob: user?.dob || "",
    height_cm: user?.height_cm ?? "",
    weight_kg: user?.weight_kg ?? "",
    gender: user?.gender || "",
    experience_level: user?.experience_level || "",
    ftp: user?.ftp ?? "",
    max_hr: user?.max_hr ?? "",
    resting_hr: user?.resting_hr ?? "",
    has_power_meter:
      user?.has_power_meter === true ? "yes" : user?.has_power_meter === false ? "no" : "",
    unit_system: user?.unit_system || "",
  };
}

function Field({ label, error, children }) {
  return (
    <label className="block min-w-0">
      <div className="card-label mb-2">{label}</div>
      {children}
      {error ? <p className="mt-1 text-xs text-analytics-amber">{error}</p> : null}
    </label>
  );
}

export const OnboardingPopup = ({ open, onClose, onUploadClick, refresh }) => {
  const { user } = useAuth();
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [stravaView, setStravaView] = useState(null);
  const wasOpen = useRef(false);

  useEffect(() => {
    if (!open) {
      wasOpen.current = false;
      return;
    }
    if (!wasOpen.current) {
      wasOpen.current = true;
      setForm(formFromUser(user));
      setErrors({});
    }
    stravaStatus()
      .then((status) => setStravaView(athleteStravaView(status)))
      .catch(() => setStravaView(null));
    // Refreshing the profile must not wipe answers the athlete is typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const update = (patch) => {
    setForm((current) => {
      const next = { ...current, ...patch };
      writeOnboardingDraft(user?.user_id, next);
      return next;
    });
  };

  const unitSystem = form.unit_system || "metric";
  const weightDisplay = (() => {
    if (form.weight_kg === "" || form.weight_kg == null) return "";
    const kg = Number(form.weight_kg);
    if (!Number.isFinite(kg)) return "";
    const value = weightValue(kg, unitSystem);
    return Number.isFinite(value) ? value.toFixed(1) : "";
  })();
  const cycling = form.default_sport === "cycling";

  const dismiss = async () => {
    setSaving(true);
    try {
      await authUpdateProfile({ onboarding_status: "dismissed" });
      clearOnboardingDraft(user?.user_id);
      await refresh?.();
      onClose?.();
    } catch {
      toast.error("Could not close setup. Your entries are still here.");
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    const required = {
      default_sport: "Choose a preferred sport.",
      dob: "Enter a date of birth.",
      height_cm: "Enter a height.",
      weight_kg: "Enter a weight.",
      gender: "Choose a gender.",
      experience_level: "Choose an experience level.",
    };
    const nextErrors = {};
    Object.entries(required).forEach(([field, message]) => {
      if (form[field] === "" || form[field] == null) nextErrors[field] = message;
    });
    const profileForm = {
      default_sport: form.default_sport,
      dob: form.dob,
      height_cm: form.height_cm,
      weight_kg: form.weight_kg,
      gender: form.gender,
      experience_level: form.experience_level,
      max_hr: form.max_hr,
      resting_hr: form.resting_hr,
      unit_system: form.unit_system,
    };
    if (cycling) {
      profileForm.ftp = form.ftp;
      profileForm.has_power_meter = form.has_power_meter;
    }
    const built = buildProfileUpdate(profileForm);
    const rangeErrors = built.errors || (built.field ? { [built.field]: built.error } : {});
    const combined = { ...nextErrors, ...profileFieldErrors(built.payload || {}), ...rangeErrors };
    if (Object.keys(combined).length || built.error) {
      setErrors(combined);
      return;
    }
    setSaving(true);
    setErrors({});
    try {
      await authUpdateProfile({ ...built.payload, onboarding_status: "complete" });
      clearOnboardingDraft(user?.user_id);
      await refresh?.();
      onClose?.();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (Array.isArray(detail)) {
        const apiErrors = {};
        detail.forEach((item) => {
          const field = Array.isArray(item?.loc) ? item.loc.filter((part) => part !== "body").join(".") : "";
          if (field) apiErrors[field] = item.msg || "Check this value.";
        });
        setErrors(apiErrors);
      } else {
        toast.error("Profile could not be saved. Your entries are still here.");
      }
    } finally {
      setSaving(false);
    }
  };

  const connectStrava = async () => {
    writeOnboardingDraft(user?.user_id, form);
    setConnecting(true);
    try {
      const { url } = await stravaConnect();
      window.location.href = url;
    } catch {
      toast.error("Could not start Strava connection");
      setConnecting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) dismiss(); }}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto overflow-x-hidden w-[min(100%-1.5rem,42rem)] max-w-none border-[var(--border-primary)] bg-[var(--surface-primary)] p-5 sm:p-6 [&>button]:flex [&>button]:h-11 [&>button]:w-11 [&>button]:items-center [&>button]:justify-center"
        data-testid="onboarding-popup"
        onPointerDownOutside={(event) => event.preventDefault()}
      >
        <DialogTitle className="text-xl text-dark-text-primary pr-12">Welcome to Endurava</DialogTitle>
        <DialogDescription className="text-sm text-dark-text-secondary">
          Add a profile when you are ready. Connecting activities is optional.
        </DialogDescription>

        <section className="space-y-3" data-testid="onboarding-activities">
          <h3 className="text-sm font-semibold text-dark-text-primary">Connect activities</h3>
          <p className="text-xs text-dark-text-muted">
            {stravaView?.label ? `Strava: ${stravaView.label}` : "Strava is not connected yet."}
          </p>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <StravaConnectButton onClick={connectStrava} connecting={connecting} />
            <button
              type="button"
              className="btn-primary-app px-4 py-3 text-xs tracking-[0.14em] uppercase min-h-[44px]"
              onClick={onUploadClick}
              data-testid="onboarding-upload"
            >
              <UploadCloud className="w-4 h-4" aria-hidden="true" />
              Upload FIT, GPX, or TCX
            </button>
          </div>
        </section>

        <section className="space-y-4" data-testid="onboarding-profile">
          <h3 className="text-sm font-semibold text-dark-text-primary">Athlete profile</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Preferred sport" error={errors.default_sport}>
              <select className={inputCls} value={form.default_sport} data-testid="onboarding-sport" onChange={(event) => update({ default_sport: event.target.value })}>
                <option value="">Select sport</option>
                <option value="cycling">Cycling</option>
                <option value="running">Running</option>
                <option value="swimming">Swimming</option>
                <option value="walking">Walking</option>
                <option value="hiking">Hiking</option>
                <option value="other">Other</option>
              </select>
            </Field>
            <Field label="Date of birth" error={errors.dob}>
              <input type="date" className={inputCls} value={form.dob} data-testid="onboarding-dob" onChange={(event) => update({ dob: event.target.value })} />
            </Field>
            <Field label="Height (cm)" error={errors.height_cm}>
              <input type="number" step="0.1" className={inputCls} value={form.height_cm} data-testid="onboarding-height" onChange={(event) => update({ height_cm: event.target.value })} />
            </Field>
            <Field label={`Weight (${unitSystem === "imperial" ? "lb" : "kg"})`} error={errors.weight_kg}>
              <input
                type="number"
                step="0.1"
                className={inputCls}
                value={weightDisplay}
                data-testid="onboarding-weight"
                onChange={(event) => {
                  const raw = event.target.value;
                  if (raw === "") {
                    update({ weight_kg: "" });
                    return;
                  }
                  const num = Number(raw);
                  if (!Number.isFinite(num)) return;
                  update({ weight_kg: toKg(num, unitSystem) });
                }}
              />
            </Field>
            <Field label="Gender" error={errors.gender}>
              <select className={inputCls} value={form.gender} data-testid="onboarding-gender" onChange={(event) => update({ gender: event.target.value })}>
                <option value="">Select</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </Field>
            <Field label="Experience level" error={errors.experience_level}>
              <select className={inputCls} value={form.experience_level} data-testid="onboarding-experience" onChange={(event) => update({ experience_level: event.target.value })}>
                <option value="">Select</option>
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
                <option value="expert">Expert</option>
              </select>
            </Field>
          </div>
        </section>

        <section className="space-y-4" data-testid="onboarding-performance">
          <h3 className="text-sm font-semibold text-dark-text-primary">Performance and display</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {cycling ? (
              <Field label="FTP (watts)" error={errors.ftp}>
                <input type="number" className={inputCls} value={form.ftp} data-testid="onboarding-ftp" placeholder="Leave blank if unknown" onChange={(event) => update({ ftp: event.target.value })} />
              </Field>
            ) : null}
            <Field label="Maximum heart rate" error={errors.max_hr}>
              <input type="number" className={inputCls} value={form.max_hr} data-testid="onboarding-max-hr" placeholder="Leave blank if unknown" onChange={(event) => update({ max_hr: event.target.value })} />
            </Field>
            <Field label="Resting heart rate" error={errors.resting_hr}>
              <input type="number" className={inputCls} value={form.resting_hr} data-testid="onboarding-resting-hr" placeholder="Leave blank if unknown" onChange={(event) => update({ resting_hr: event.target.value })} />
            </Field>
            {cycling ? (
              <Field label="Power meter" error={errors.has_power_meter}>
                <select className={inputCls} value={form.has_power_meter} data-testid="onboarding-power-meter" onChange={(event) => update({ has_power_meter: event.target.value })}>
                  <option value="">Unknown</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </Field>
            ) : null}
            <Field label="Measurement units">
              <select className={inputCls} value={form.unit_system} data-testid="onboarding-units" onChange={(event) => update({ unit_system: event.target.value })}>
                <option value="">Select</option>
                <option value="metric">Metric</option>
                <option value="imperial">Imperial</option>
              </select>
            </Field>
          </div>
        </section>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-2">
          <button type="button" className="min-h-[44px] px-4 text-sm text-dark-text-secondary" onClick={dismiss} data-testid="onboarding-skip">
            Skip for now
          </button>
          <button type="button" className="btn-primary-app px-6 py-3 text-xs tracking-[0.14em] uppercase min-h-[44px]" onClick={save} disabled={saving} data-testid="onboarding-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Save profile
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
